import { getTheme } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPIcon, IPStack, IPText } from 'baseComponents';
import { globalStackTokensGapExtraSmall, globalStackTokensGapSmall, globalTextStylesBold } from 'globalStyles';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { colorMixer } from 'utils/color';

export interface IGenericApprovalButtonProps {
  compact?: boolean;
  disabled?: boolean;
  icon: JSX.Element;
  sideText: string;
  selected: boolean;
  selectedColor: string;
  onClick: () => void;
}

/** The fills the approve/reject pair uses when selected; other two-way choices reuse them. */
export const approvalSelectedColors = { approve: '#6fc98f', reject: '#eb5752' };

/** A two-way choice button: the selected one is filled. Exported for other yes/no pairs. */
export const ApprovalButton = (props: IGenericApprovalButtonProps) => {
  const appContext = useContext(AppContext);
  const theme = getTheme();

  const getButtonBackground = (): string => {
    if (props.disabled && props.selected) {
      const baseColor = appContext.useDarkMode ? theme.palette.neutralDark : theme.palette.neutralLight;

      return colorMixer(baseColor, props.selectedColor, 0.5);
    } else if (props.disabled) {
      return appContext.useDarkMode ? theme.palette.neutralDark : theme.palette.neutralLight;
    } else if (props.selected) {
      return props.selectedColor;
    }

    return appContext.useDarkMode ? theme.palette.neutralPrimary : theme.palette.themeLighterAlt;
  };

  return (
    <IPStack
      tabIndex={0}
      role="button"
      aria-pressed={props.selected}
      aria-disabled={props.disabled ? true : undefined}
      onKeyDown={(ev) => {
        if (!props.disabled && (ev.key === 'Enter' || ev.key === ' ')) {
          props.onClick();
        }
      }}
      onClick={() => {
        if (!props.disabled) {
          props.onClick();
        }
      }}
      styles={{
        root: {
          // A minimum, not a fixed width: a longer label ("Buiten beschouwing") widens the button
          // instead of wrapping; Approve/Reject still land on exactly 160/120.
          minWidth: props.compact ? 120 : 160,
          height: props.compact ? 32 : 36,
          borderWidth: 1,
          borderS: '1px solid grey',
          borderRadius: '3px',
          backgroundColor: getButtonBackground(),
          padding: 5,
          // It is a button: a pointer, no text selection, and the hover tint from the stylesheet.
          cursor: props.disabled ? 'default' : 'pointer',
          userSelect: 'none',
          selectors:
            props.disabled || props.selected
              ? undefined
              : {
                  ':hover': {
                    backgroundColor: appContext.useDarkMode ? theme.palette.neutralPrimaryAlt : theme.palette.themeLight,
                  },
                },
        },
      }}
    >
      <IPStack.Item>
        <IPStack
          horizontal
          verticalAlign="center"
          tokens={props.compact ? globalStackTokensGapExtraSmall : globalStackTokensGapSmall}
        >
          <IPStack.Item styles={{ root: { paddingTop: 2 } }}>{props.icon}</IPStack.Item>
          <IPStack.Item>
            <IPText styles={globalTextStylesBold}>{props.sideText}</IPText>
          </IPStack.Item>
        </IPStack>
      </IPStack.Item>
    </IPStack>
  );
};

interface IApprovalButtonProps {
  selected: boolean;
  disabled?: boolean;
  compact?: boolean;
  onClick: () => void;
}

export const ApprovalButtonApprove = (props: IApprovalButtonProps) => {
  const { t } = useTranslation(['library']);
  const iconSize = props.compact ? 18 : 24;

  return (
    <ApprovalButton
      compact={props.compact}
      disabled={props.disabled}
      onClick={props.onClick}
      icon={<IPIcon iconName="Checkmark" style={{ fontSize: iconSize }} />}
      selected={props.selected}
      sideText={t('library:ApprovalTasks.ApprovalTaskDetail.ApproveActions.Approve')}
      selectedColor={approvalSelectedColors.approve}
    />
  );
};

export const ApprovalButtonReject = (props: IApprovalButtonProps) => {
  const { t } = useTranslation(['library']);
  const iconSize = props.compact ? 18 : 24;

  return (
    <ApprovalButton
      compact={props.compact}
      disabled={props.disabled}
      onClick={props.onClick}
      icon={<IPIcon iconName="Cancel" style={{ fontSize: iconSize }} />}
      selected={props.selected}
      sideText={t('library:ApprovalTasks.ApprovalTaskDetail.ApproveActions.Reject')}
      selectedColor={approvalSelectedColors.reject}
    />
  );
};
